import React, { Component, Fragment } from 'react';
import {
  Card,
  Col,
  Row,
  Spin,
  notification,
  Select,
  Table,
  Typography,
  Button,
  Switch,
  Space,
} from 'antd';
import { DealershipChooser } from 'components';
import { get } from 'lodash';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { ShopStore } from 'stores';
import styled from 'styled-components';
import { webServiceProvider } from 'shared';
import AppStore from 'stores/App.js';
import { DatabaseOutlined, TeamOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Title } = Typography;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 20px;
`;

const FiltersContainer = styled.div`
  margin: 16px 0;
  display: flex;
  gap: 16px;
  align-items: center;
`;

class DataMinerPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
  };

  state = {
    isLoading: false,
    isExporting: false,
    data: [],
    groupBy: 'work_order',
    totalRecords: 0,
    includeSubFleets: true,
    subFleetCount: 0,
    currentPage: 1,
    pageSize: 100,
    totalPages: 0,
  };

  componentDidMount() {
    AppStore.setSelectedSidebarKey('/data-miner');
    this.init();
  }

  componentWillUnmount() {
    this.disposer();
  }

  disposer = observe(ShopStore.currentShop, 'id', () => {
    this.init();
  });

  init = async () => {
    if (!ShopStore.currentShop?.id) return;
    this.loadData();
  };

  loadData = async (page = 1) => {
    try {
      this.setState({ isLoading: true });
      const { groupBy, includeSubFleets, pageSize } = this.state;

      console.log(`Loading Data Miner - Page: ${page}, PageSize: ${pageSize}`);

      const response = await webServiceProvider.getMany('v1/analytics', {
        shopId: ShopStore.currentShop.id,
        groupBy: groupBy,
        includeSubFleets: includeSubFleets,
        page: page,
        pageSize: pageSize,
      });

      const data = get(response, 'data', []);
      const total = get(response, 'meta.total', 0);
      const totalPages = get(response, 'meta.totalPages', 1);

      // Debug logging to understand data structure
      if (data.length > 0) {
        console.log('[Data Miner Debug] Sample record structure:', {
          firstRecord: data[0],
          availableFields: Object.keys(data[0]),
          shopName: data[0].shop_name,
          vendorName: data[0].vendor_name,
          vendorId: data[0].vendor_id,
          hasVendorName: 'vendor_name' in data[0],
        });

        // Check for vendor-related fields
        const vendorFields = Object.keys(data[0]).filter((key) =>
          key.toLowerCase().includes('vendor')
        );
        if (vendorFields.length > 0) {
          console.log('[Data Miner Debug] Vendor fields found:', vendorFields);
        }
      }

      this.setState({
        data,
        totalRecords: total,
        currentPage: page,
        totalPages,
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.message || 'Failed to load data',
      });
      this.setState({
        data: [],
        totalRecords: 0,
        totalPages: 0,
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleGroupByChange = (value) => {
    this.setState({ groupBy: value }, () => {
      this.loadData();
    });
  };

  handleIncludeSubFleetsChange = (checked) => {
    this.setState({ includeSubFleets: checked, currentPage: 1 }, () => {
      this.loadData(1);
    });
  };

  handlePageChange = (page, pageSize) => {
    this.setState({ currentPage: page, pageSize }, () => {
      this.loadData(page);
    });
  };

  handlePageSizeChange = (current, size) => {
    this.setState({ pageSize: size, currentPage: 1 }, () => {
      this.loadData(1);
    });
  };

  convertToCSV = (data, columns) => {
    // Helper function to properly escape CSV values
    const escapeCSVValue = (value) => {
      // Convert to string and handle null/undefined
      if (value === null || value === undefined || value === '') {
        return '';
      }

      // Convert to string if not already
      let stringValue = typeof value === 'string' ? value : String(value);

      // Check if value needs escaping
      // Escape if contains: comma, quote, newline, carriage return, or tab
      if (
        stringValue.includes(',') ||
        stringValue.includes('"') ||
        stringValue.includes('\n') ||
        stringValue.includes('\r') ||
        stringValue.includes('\t')
      ) {
        // Escape quotes by doubling them
        stringValue = stringValue.replace(/"/g, '""');
        // Wrap in quotes
        return `"${stringValue}"`;
      }

      return stringValue;
    };

    // Process headers
    const csvHeaders = columns.map((col) => escapeCSVValue(col.title));

    // Process data rows
    const csvRows = data.map((row) => {
      return columns.map((col) => {
        let value = '';

        try {
          // Get the raw value
          if (Array.isArray(col.dataIndex)) {
            // Handle nested dataIndex like ['work_orders', 0, 'status']
            value = get(row, col.dataIndex, '');
          } else {
            value = row[col.dataIndex];
          }

          // Handle special cases for rendering
          if (col.render && typeof col.render === 'function') {
            if (col.key === 'work_orders_count') {
              value = value?.length || 0;
            } else if (col.key?.includes('cost') || col.key?.includes('Cost')) {
              // Format cost values
              if (value !== null && value !== undefined && value !== '') {
                const numValue = parseFloat(value);
                value = isNaN(numValue) ? '$0.00' : `$${numValue.toFixed(2)}`;
              } else {
                value = '$0.00';
              }
            } else if (col.key?.includes('_at') || col.key?.includes('At')) {
              // Format date values
              if (value) {
                try {
                  const date = new Date(value);
                  value = isNaN(date.getTime())
                    ? ''
                    : date.toLocaleDateString();
                } catch (e) {
                  value = '';
                }
              } else {
                value = '';
              }
            }
          }

          // Apply CSV escaping
          return escapeCSVValue(value);
        } catch (error) {
          return '';
        }
      });
    });

    // Use comma as delimiter (standard CSV format)
    const csvContent = [csvHeaders, ...csvRows]
      .map((row) => row.join(','))
      .join('\n');

    // Add BOM for proper UTF-8 encoding in Excel
    const BOM = '\uFEFF';
    return BOM + csvContent;
  };

  exportToCSV = async () => {
    const { groupBy, totalRecords, includeSubFleets } = this.state;

    // Set exporting state to disable interactions
    this.setState({ isExporting: true });

    // Debug: Log export start
    console.log('[CSV Export] Starting export with:', {
      groupBy,
      totalRecords,
      includeSubFleets,
      timestamp: new Date().toISOString(),
    });

    // For large exports, fetch all data
    if (totalRecords > 100) {
      // Show non-dismissible notification
      notification.info({
        message: 'Exporting Data',
        description: (
          <div>
            <div>Fetching all {totalRecords} records for export...</div>
            <div style={{ marginTop: 8 }}>
              <Spin size="small" /> Please wait, do not navigate away
            </div>
          </div>
        ),
        duration: 0, // Don't auto-close
        key: 'export-progress',
        closeIcon: <span />, // Hide close button
      });

      try {
        // Fetch all data in one request for export
        const response = await webServiceProvider.getMany('v1/analytics', {
          shopId: ShopStore.currentShop.id,
          groupBy: groupBy,
          includeSubFleets: includeSubFleets,
          page: 1,
          pageSize: Math.min(totalRecords, 10000), // Cap at 10k for safety
        });

        const allData = get(response, 'data', []);
        const columns = this.getTableColumns();

        // Debug: Log data before conversion
        console.log('[CSV Export] Converting data:', {
          recordCount: allData.length,
          columnCount: columns.length,
          columns: columns.map((c) => ({
            key: c.key,
            title: c.title,
            dataIndex: c.dataIndex,
          })),
          sampleRecord: allData[0],
        });

        const csvContent = this.convertToCSV(allData, columns);

        // Debug: Verify CSV content
        const lines = csvContent.split('\n');
        console.log('[CSV Export] CSV generated:', {
          totalLines: lines.length,
          headerLine: lines[0]?.substring(0, 200),
          firstDataLine: lines[1]?.substring(0, 200),
        });

        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `data-miner-${groupBy}-${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success notification
        notification.success({
          message: 'Export Complete',
          description: `Successfully exported ${allData.length} records`,
          key: 'export-progress',
          duration: 4,
        });
      } catch (error) {
        notification.error({
          message: 'Export Failed',
          description: error.message || 'Failed to export data',
          key: 'export-progress',
          duration: 4,
        });
      } finally {
        // Always reset exporting state
        this.setState({ isExporting: false });
      }
    } else {
      // For small datasets, use current page data
      try {
        const { data } = this.state;
        const columns = this.getTableColumns();

        // Debug: Log data before conversion
        console.log('[CSV Export] Converting data (small dataset):', {
          recordCount: data.length,
          columnCount: columns.length,
          columns: columns.map((c) => ({
            key: c.key,
            title: c.title,
            dataIndex: c.dataIndex,
          })),
          sampleRecord: data[0],
        });

        const csvContent = this.convertToCSV(data, columns);

        // Debug: Verify CSV content
        const lines = csvContent.split('\n');
        console.log('[CSV Export] CSV generated (small dataset):', {
          totalLines: lines.length,
          headerLine: lines[0]?.substring(0, 200),
          firstDataLine: lines[1]?.substring(0, 200),
        });

        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute(
          'download',
          `data-miner-${groupBy}-${new Date().toISOString().split('T')[0]}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notification.success({
          message: 'Export Complete',
          description: `Successfully exported ${data.length} records`,
          duration: 3,
        });
      } finally {
        // Reset exporting state
        this.setState({ isExporting: false });
      }
    }
  };

  getTableColumns = () => {
    const { groupBy } = this.state;

    if (groupBy === 'asset') {
      return [
        {
          title: 'Fleet Name',
          dataIndex: 'shop_name',
          key: 'shop_name',
        },
        {
          title: 'Asset ID',
          dataIndex: 'asset_id',
          key: 'asset_id',
        },
        {
          title: 'Asset Name',
          dataIndex: 'asset_name',
          key: 'asset_name',
        },
        {
          title: 'Asset Type',
          dataIndex: 'asset_type',
          key: 'asset_type',
        },
        {
          title: 'Make',
          dataIndex: 'make',
          key: 'make',
        },
        {
          title: 'Model',
          dataIndex: 'model',
          key: 'model',
        },
        {
          title: 'Year',
          dataIndex: 'year',
          key: 'year',
        },
        {
          title: 'VIN',
          dataIndex: 'vin',
          key: 'vin',
        },
        {
          title: 'License Plate',
          dataIndex: 'license_plate',
          key: 'license_plate',
        },
        {
          title: 'Work Order Status',
          dataIndex: ['work_orders', 0, 'status_description'],
          key: 'work_order_status_description',
        },
        {
          title: 'Priority',
          dataIndex: ['work_orders', 0, 'priority'],
          key: 'work_order_priority',
        },
        {
          title: 'Line Total Cost',
          dataIndex: ['work_orders', 0, 'line_total_cost'],
          key: 'work_order_line_total_cost',
          render: (cost) => `$${parseFloat(cost).toFixed(2)}`,
        },
        {
          title: 'Car Status',
          dataIndex: 'car_status_description',
          key: 'car_status_description',
        },
        {
          title: 'Work Orders Count',
          dataIndex: 'work_orders',
          key: 'work_orders_count',
          render: (workOrders) => workOrders?.length || 0,
        },
      ];
    }

    // Default work_order columns
    return [
      {
        title: 'Fleet Name',
        dataIndex: 'shop_name',
        key: 'shop_name',
      },
      {
        title: 'Vendor Name',
        dataIndex: 'vendor_name',
        key: 'vendor_name',
      },
      {
        title: 'Work Order ID',
        dataIndex: 'work_order_id',
        key: 'work_order_id',
      },
      {
        title: 'Invoice Number',
        dataIndex: 'invoice_number',
        key: 'invoice_number',
      },
      {
        title: 'Car Name',
        dataIndex: 'car_name',
        key: 'car_name',
      },
      {
        title: 'Asset Type',
        dataIndex: 'asset_type',
        key: 'asset_type',
      },
      {
        title: 'Status',
        dataIndex: 'status_description',
        key: 'status_description',
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Mileage',
        dataIndex: 'mileage',
        key: 'mileage',
      },
      {
        title: 'Engine Hours',
        dataIndex: 'engine_hours',
        key: 'engine_hours',
      },
      {
        title: 'Created By',
        dataIndex: 'created_by',
        key: 'created_by',
      },
      {
        title: 'Tax',
        dataIndex: 'tax',
        key: 'tax',
      },
      {
        title: 'Tax Type',
        dataIndex: 'tax_type',
        key: 'tax_type',
      },
      {
        title: 'Misc',
        dataIndex: 'misc',
        key: 'misc',
      },
      {
        title: 'Misc Type',
        dataIndex: 'misc_type',
        key: 'misc_type',
      },
      {
        title: 'Line Parts Cost',
        dataIndex: 'line_parts_cost',
        key: 'line_parts_cost',
      },
      {
        title: 'Line Labour Cost',
        dataIndex: 'line_labour_cost',
        key: 'line_labour_cost',
      },
      {
        title: 'Total Cost',
        dataIndex: 'line_total_cost',
        key: 'line_total_cost',
        render: (cost) => (cost ? `$${parseFloat(cost).toFixed(2)}` : '$0.00'),
      },
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        render: (date) => (date ? new Date(date).toLocaleDateString() : ''),
      },
      {
        title: 'Car Make',
        dataIndex: 'car_make',
        key: 'car_make',
      },
      {
        title: 'Car Model',
        dataIndex: 'car_model',
        key: 'car_model',
      },
      {
        title: 'Car Year',
        dataIndex: 'car_year',
        key: 'car_year',
      },
      {
        title: 'VIN',
        dataIndex: 'car_vin',
        key: 'car_vin',
      },
      {
        title: 'Car License Plate',
        dataIndex: 'car_license_plate',
        key: 'car_license_plate',
      },
      {
        title: 'Updated At',
        dataIndex: 'updated_at',
        key: 'updated_at',
        render: (date) => (date ? new Date(date).toLocaleDateString() : ''),
      },
      {
        title: 'Reported At',
        dataIndex: 'reported_at',
        key: 'reported_at',
        render: (date) => (date ? new Date(date).toLocaleDateString() : ''),
      },
      {
        title: 'Warranty Savings',
        dataIndex: 'warranty_savings',
        key: 'warranty_savings',
      },
      {
        title: 'Line ID',
        dataIndex: 'work_order_line_id',
        key: 'work_order_line_id',
      },
      {
        title: 'Line Title',
        dataIndex: 'line_title',
        key: 'line_title',
      },
      {
        title: 'Line Description',
        dataIndex: 'line_description',
        key: 'line_description',
      },
      {
        title: 'Car ID',
        dataIndex: 'id_car',
        key: 'id_car',
      },
      {
        title: 'Shop ID',
        dataIndex: 'id_shop',
        key: 'id_shop',
      },
      {
        title: 'Car Created At',
        dataIndex: 'car_created_at',
        key: 'car_created_at',
        render: (date) => (date ? new Date(date).toLocaleDateString() : ''),
      },
      {
        title: 'Car Updated At',
        dataIndex: 'car_updated_at',
        key: 'car_updated_at',
        render: (date) => (date ? new Date(date).toLocaleDateString() : ''),
      },
      {
        title: 'Car Status',
        dataIndex: 'car_status_description',
        key: 'car_status_description',
      },
    ];
  };

  render() {
    const {
      isLoading,
      isExporting,
      data,
      groupBy,
      totalRecords,
      includeSubFleets,
      currentPage,
      pageSize,
    } = this.state;

    return (
      <Container>
        <Fragment>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Title level={1}>
                <DatabaseOutlined style={{ marginRight: 8 }} />
                Data Miner
              </Title>
            </Col>
          </Row>

          <DealershipChooser />

          <FiltersContainer>
            <div>
              <label style={{ marginRight: 8, fontWeight: 'bold' }}>
                Group By:
              </label>
              <Select
                value={groupBy}
                onChange={this.handleGroupByChange}
                style={{ width: 200 }}
                disabled={isExporting}
              >
                <Option value="work_order">Work Order</Option>
                <Option value="asset">Asset</Option>
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TeamOutlined />
              <label style={{ fontWeight: 'bold' }}>Include Sub-Fleets:</label>
              <Switch
                checked={includeSubFleets}
                onChange={this.handleIncludeSubFleetsChange}
                checkedChildren="Yes"
                unCheckedChildren="No"
                disabled={isExporting}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button
                type="primary"
                onClick={this.exportToCSV}
                disabled={isLoading || data.length === 0 || isExporting}
                loading={isExporting}
              >
                {isExporting ? 'Exporting...' : 'Export to CSV'}
              </Button>
              <span style={{ color: '#666' }}>
                Total Records: {totalRecords}
              </span>
            </div>
          </FiltersContainer>

          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Spin
                spinning={isLoading || isExporting}
                tip={isExporting ? 'Exporting data...' : 'Loading...'}
              >
                <Card>
                  <Table
                    dataSource={data}
                    columns={this.getTableColumns()}
                    rowKey={(record, index) => {
                      if (groupBy === 'asset') {
                        return record.asset_id || `asset-${index}`;
                      } else {
                        // For work orders, create a unique key using multiple fields
                        const workOrderId = record.work_order_id || 'no-wo-id';
                        const lineId = record.work_order_line_id || 'no-line';
                        return `${workOrderId}-${lineId}-${index}`;
                      }
                    }}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalRecords,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`,
                      pageSizeOptions: ['50', '100', '200', '500'],
                      onChange: this.handlePageChange,
                      onShowSizeChange: this.handlePageSizeChange,
                      disabled: isExporting, // Disable pagination during export
                    }}
                    scroll={{ x: 'max-content' }}
                    size="small"
                  />
                </Card>
              </Spin>
            </Col>
          </Row>
        </Fragment>
      </Container>
    );
  }
}

export default withRouter(observer(DataMinerPage));
